import { destroy, get, patch, post, put } from '@/api/axios';
import { AnnouncementEndpoints } from '@/api/endpoints/AnnouncementEndpoints';
import { ContactEndpoints } from '@/api/endpoints/ContactEndpoints';
import { UserRoleEndpoints } from '@/api/endpoints/UserRoleEndpoints';
import { type AnnouncementDto } from '@/app/models/AnnouncementDto';
import { type AnnouncementPayload } from '@/app/models/AnnouncementPayload';
import { type AnnouncementValidityPayload } from '@/app/models/AnnouncementValidityPayload';
import { type ContactDto, ContactType } from '@/app/models/Contact';
import { type ListData } from '@/app/models/ListData';
import { type UserRoleDto } from '@/app/models/UserRoleDto';
import { type ApiResponse } from '@/api/core/_models';
import { type AttachmentDto } from '@/app/models/AttachmentDto';
import { uploadAttachment } from '@/app/modules/attachments/core/_requests';
import { type AttachmentUploadProgress } from '@/app/modules/attachments/core/_models';

export const uploadMedia = (
  file: File,
  signal?: AbortSignal,
  onUploadProgress?: AttachmentUploadProgress,
): Promise<ApiResponse<AttachmentDto>> =>
  uploadAttachment(file, {
    endpoint: AnnouncementEndpoints.uploadMedia,
    temporary: true,
    local: false,
    signal,
    onUploadProgress,
  });

// Os tipos de contato que entram no painel, os mesmos que o backend conta no público.
const AUDIENCE_CONTACT_TYPES: ContactType[] = [
  ContactType.SYSTEM_USER,
  ContactType.ACCOUNTANT,
  ContactType.COMMUNITY,
  ContactType.ADMIN,
];
// O seletor filtra na tela: a lista inteira do ambiente vem de uma vez.
const AUDIENCE_PAGE_SIZE = 1000;

export const getDetails = (id: string) => get<AnnouncementDto>(AnnouncementEndpoints.details(id));

// Com `shouldPublish`, o backend salva e publica na mesma transação; sem ele, fica rascunho.
export const add = (payload: AnnouncementPayload, shouldPublish: boolean) =>
  post<AnnouncementDto>(AnnouncementEndpoints.create, payload, { params: { publish: shouldPublish } });

export const edit = (id: string, payload: AnnouncementPayload, shouldPublish: boolean) =>
  put<AnnouncementDto>(AnnouncementEndpoints.edit(id), payload, { params: { publish: shouldPublish } });

export const remove = (id: string) => destroy<string>(AnnouncementEndpoints.delete(id));

export const publish = (id: string) => patch<AnnouncementDto>(AnnouncementEndpoints.publish(id));

export const updateValidity = (id: string, payload: AnnouncementValidityPayload) =>
  patch<AnnouncementDto>(AnnouncementEndpoints.validity(id), payload);

export const close = (id: string) => patch<AnnouncementDto>(AnnouncementEndpoints.close(id));

export const listAudienceContacts = () =>
  post<ListData<ContactDto>>(ContactEndpoints.list, {
    search: '',
    per_page: AUDIENCE_PAGE_SIZE,
    sort: 'name',
    direction: 'ASC',
    filter: { types: AUDIENCE_CONTACT_TYPES },
  });

export const listAudienceRoles = () =>
  post<ListData<UserRoleDto>>(UserRoleEndpoints.list, {
    search: '',
    per_page: AUDIENCE_PAGE_SIZE,
    sort: 'name',
    direction: 'ASC',
  });
